import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import Icon from './Icon'
import { current, goStep, isDone, offersOff, setOffersOff, snooze, snoozed, startTour, stopTour, subscribe, topics, ui, type Topic } from '../lib/tour'

/** The tour card and spotlight, plus the first-visit offer. Mounted once, near the top of the app. */
export default function Tour({ ready }: { ready: boolean }) {
  const run = useSyncExternalStore(subscribe, current, current)
  const [hash, setHash] = useState(() => location.hash)
  useEffect(() => {
    const on = () => setHash(location.hash)
    window.addEventListener('hashchange', on)
    return () => window.removeEventListener('hashchange', on)
  }, [])
  const topic = run ? topics().find(t => t.id === run.id) : undefined
  if (run && topic) return <TourCard topic={topic} index={run.index}/>
  return ready ? <Offer hash={hash}/> : null
}

function Offer({ hash }: { hash: string }) {
  const [shown, setShown] = useState<Topic | null>(null)
  const [, bump] = useState(0)
  useEffect(() => {
    setShown(null)
    if (offersOff() || /^#\/(auth|join)/.test(hash)) return
    const match = topics().find(t => t.match(hash))
    if (!match || isDone(match.id) || snoozed(match.id)) return
    const timer = window.setTimeout(() => setShown(match), 1200)
    return () => window.clearTimeout(timer)
  }, [hash])
  if (!shown) return null
  const u = ui()
  const close = () => { snooze(shown.id); setShown(null); bump(n => n + 1) }
  return <div className="tour-offer" role="dialog" aria-label={u.offer}>
    <p><strong>{shown.title}</strong><br/>{u.offer}</p>
    <div className="tour-actions">
      <button type="button" className="tour-primary" onClick={() => { const id = shown.id; setShown(null); startTour(id) }}>{u.show}</button>
      <button type="button" onClick={close}>{u.later}</button>
      <button type="button" className="tour-quiet" onClick={() => { setOffersOff(); setShown(null) }}>{u.never}</button>
    </div>
  </div>
}

type Box = { left: number; top: number; width: number; height: number }
function TourCard({ topic, index }: { topic: Topic; index: number }) {
  const step = topic.steps[index]!
  const u = ui()
  const [box, setBox] = useState<Box | null>(null)
  const [place, setPlace] = useState<React.CSSProperties>({})
  const card = useRef<HTMLDivElement>(null)
  const last = index === topic.steps.length - 1

  const measure = useCallback(() => {
    const el = step.sel ? document.querySelector<HTMLElement>(step.sel) : null
    const r = el?.getBoundingClientRect()
    const visible = Boolean(el && r && r.width > 4 && r.height > 4 && getComputedStyle(el).visibility !== 'hidden')
    const cardH = card.current?.offsetHeight ?? 190
    const vw = window.innerWidth, vh = window.innerHeight
    if (!visible || !r) { setBox(null); setPlace({}); return }
    const pad = 6
    setBox({ left: r.left - pad, top: r.top - pad, width: r.width + pad * 2, height: r.height + pad * 2 })
    if (vw < 640) { setPlace({}); return } // phones: the card is a bottom sheet
    const width = Math.min(380, vw - 24)
    const left = Math.max(12, Math.min(r.left, vw - width - 12))
    const below = r.bottom + 14
    const top = below + cardH < vh - 12 ? below : Math.max(12, r.top - cardH - 14)
    setPlace({ left, top, width })
  }, [step.sel])

  useLayoutEffect(() => {
    const el = step.sel ? document.querySelector<HTMLElement>(step.sel) : null
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
      if (window.innerWidth < 640) { const r = el.getBoundingClientRect(); const limit = window.innerHeight - (card.current?.offsetHeight ?? 220) - 30; if (r.bottom > limit) window.scrollBy({ top: r.bottom - limit }) }
    }
    measure()
    const settle = window.setTimeout(measure, 380)
    return () => window.clearTimeout(settle)
  }, [step.sel, index, measure])

  useEffect(() => {
    let frame = 0
    const again = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure) }
    window.addEventListener('resize', again)
    window.addEventListener('scroll', again, true)
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', again); window.removeEventListener('scroll', again, true) }
  }, [measure])

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); stopTour(false) }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goStep(1) }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goStep(-1) }
      else if (e.key === 'Tab' && card.current) {
        const items = [...card.current.querySelectorAll<HTMLElement>('button:not([disabled])')]
        if (!items.length) return
        const first = items[0]!, end = items[items.length - 1]!
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); end.focus() }
        else if (!e.shiftKey && document.activeElement === end) { e.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', key)
    return () => window.removeEventListener('keydown', key)
  }, [])

  useEffect(() => { card.current?.querySelector<HTMLElement>('.tour-primary')?.focus() }, [index])

  return <div className="tour-layer">
    <div className="tour-block" aria-hidden="true"/>
    {box ? <div className="tour-ring" style={box} aria-hidden="true"/> : <div className="tour-dim" aria-hidden="true"/>}
    <div ref={card} className={`tour-card${box ? '' : ' tour-center'}`} style={box ? place : undefined} role="dialog" aria-modal="true" aria-labelledby="tour-title">
      <button type="button" className="tour-close" aria-label={u.stop} onClick={() => stopTour(false)}><Icon name="close" size={18}/></button>
      <p className="tour-count">{topic.title} · {u.step(index + 1, topic.steps.length)}</p>
      <h2 id="tour-title">{step.title}</h2>
      <p>{step.body}</p>
      <div className="tour-actions">
        <button type="button" disabled={index === 0} onClick={() => goStep(-1)}>{u.back}</button>
        <button type="button" className="tour-primary" onClick={() => goStep(1)}>{last ? u.done : u.next}</button>
      </div>
    </div>
  </div>
}

/** The Account page list: replay any tour. */
export function TourList() {
  const u = ui()
  return <ul className="tour-list">
    {topics().map(t => <li key={t.id}><button type="button" onClick={() => startTour(t.id)}><strong>{t.title}</strong><small>{t.blurb}</small></button></li>)}
    {offersOff() && <li><button type="button" onClick={() => { try { localStorage.removeItem('cl.tour.off') } catch { /* nothing to undo */ } startTour('welcome') }}><strong>{u.show}</strong><small>{u.offer}</small></button></li>}
  </ul>
}
